import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award, Search, X } from "lucide-react";
import api from "@/lib/api";
import { matchesAllTokens } from "@/lib/search";
import { SeoHead } from "@/components/public/SeoHead";
import { DetailModal } from "@/components/public/DetailModal";
import { AdSlot } from "@/components/public/AdSlot";

function certMatches(cert, query) {
  if (!query) return true;
  const haystack = [cert.name, cert.description].filter(Boolean).join(" ");
  return matchesAllTokens(haystack, query);
}

export default function BrandCertificationsPage() {
  const { data = [] } = useQuery({
    queryKey: ["public-brand-certifications"],
    queryFn: async () => (await api.get("/public/brand-certifications")).data,
  });
  const [openCert, setOpenCert] = useState(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => data.filter((c) => certMatches(c, query)), [data, query]);
  const trimmedQuery = query.trim();
  const isSearching = !!trimmedQuery;

  return (
    <div data-testid="brand-certifications-page">
      <SeoHead
        title="Brand certifications — Sustainably Curated"
        description="Company-level certifications brands can hold — B Corp, 1% for the Planet, Fair Trade USA, and more — explained for everyday shoppers."
        path="/brand-certifications"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Brand certifications</h1>
        <p className="text-muted_text">
          Independent standards that verify a brand's company-level practices — governance, social impact, environmental commitments, and giving. Distinct from material-level standards that certify specific fibres or products.
        </p>
      </section>

      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
          <div className="relative w-full sm:w-80" data-testid="brand-certifications-search-wrapper">
            <Search className="w-4 h-4 text-muted_text absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search brand certifications…"
              className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-line rounded-full focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20 placeholder:text-muted_text/80"
              data-testid="brand-certifications-search-input"
              aria-label="Search brand certifications"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-muted_text hover:text-ink"
                data-testid="brand-certifications-search-clear"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {isSearching && filtered.length === 0 ? (
          <p className="text-muted_text text-sm italic mt-4" data-testid="brand-certifications-search-empty">
            No brand certifications match “{trimmedQuery}”. Try a different keyword or clear the search.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((c) => <CertCard key={c.id} cert={c} onOpen={() => setOpenCert(c)} />)}
            {filtered.length === 0 && !isSearching && (
              <p className="text-muted_text col-span-full text-sm italic">No brand certifications yet.</p>
            )}
          </div>
        )}

        {openCert && <BrandCertificationModal cert={openCert} onClose={() => setOpenCert(null)} />}
      </section>

      <AdSlot slotKey="brand_certifications_mid" />
    </div>
  );
}

function CertCard({ cert, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white border border-line p-4 hover:border-forest/40 transition-colors flex flex-col gap-3"
      data-testid={`brand-cert-card-${cert.slug}`}
    >
      <div className="h-14 flex items-center">
        {cert.logo_url ? (
          <img src={cert.logo_url} alt={`${cert.name} logo`} className="max-h-14 w-auto object-contain" loading="lazy" />
        ) : (
          <Award className="w-6 h-6 text-forest" />
        )}
      </div>
      <h2 className="font-serif text-lg leading-tight" title={cert.name}>{cert.name}</h2>
      {cert.description && <p className="text-xs text-ink/85 line-clamp-3">{cert.description}</p>}
    </button>
  );
}

function BrandCertificationModal({ cert, onClose }) {
  const logo = cert.logo_url ? (
    <img src={cert.logo_url} alt={`${cert.name} logo`} className="max-h-16 w-auto object-contain" />
  ) : (
    <Award className="w-8 h-8 text-forest" />
  );

  return (
    <DetailModal
      open={Boolean(cert)}
      onClose={onClose}
      testId="brand-cert-modal"
      title={cert.name}
      badge={logo}
      externalLink={cert.external_link ? { href: cert.external_link, label: "Visit official site" } : null}
      fullPageHref={`/brand-certifications/${cert.slug}`}
    >
      {cert.description && (
        <p className="text-sm text-muted_text leading-relaxed whitespace-pre-line">{cert.description}</p>
      )}
    </DetailModal>
  );
}
